/**
 * BRICK Schema Template Protection Composable
 *
 * Provides template protection logic for the BRICK Schema Manager.
 * Implements cascade protection: Equipment → Devices → Points
 *
 * When equipment is created from a template, it cannot be modified directly.
 * This protection cascades to all child devices and points.
 *
 * @returns {Object} Template protection checking functions
 */
export function useTemplateProtection() {
  /**
   * Check if equipment is a controller (has controller_template_id or brick_class contains 'Controller')
   * @param {Object} node - Equipment node
   * @returns {boolean} True if equipment is a controller
   */
  const isController = (node) => {
    // Check for controller_template_id
    const hasControllerTemplate = !!(node.data?.controller_template_id || node.controller_template_id);

    // Check if brick_class contains 'Controller'
    const brickClass = node.data?.brick_class || node.brickClass || '';
    const hasControllerClass = brickClass.includes('Controller');

    return hasControllerTemplate || hasControllerClass;
  };

  /**
   * Check if equipment is from a template
   * @param {Object} node - Equipment node
   * @returns {boolean} True if equipment has template_id (equipment OR controller template)
   */
  const isTemplatedEquipment = (node) => {
    // Check for equipment_template_id which indicates equipment created from a template
    // OR controller_template_id which indicates controller created from a controller template
    return !!(node.data?.equipment_template_id || node.equipment_template_id ||
              node.data?.controller_template_id || node.controller_template_id ||
              node.data?.template_id || node.templateId);
  };

  /**
   * Check if device is from a template (direct OR cascaded from parent equipment)
   * @param {Object} node - Device node
   * @returns {boolean} True if device has template_id OR parent equipment has template_id
   */
  const isTemplatedDevice = (node) => {
    // Check if device itself is from a device template
    const hasDeviceTemplate = !!(node.data?.device_template_id || node.device_template_id ||
              node.data?.template_id || node.templateId ||
              node.data?.template_name || node.templateName);

    // Check if parent equipment is from an equipment template (CASCADE)
    const hasParentEquipmentTemplate = !!(node.parentEquipmentTemplateId ||
              node.data?.parentEquipmentTemplateId);

    return hasDeviceTemplate || hasParentEquipmentTemplate;
  };

  /**
   * Check if point is from a template (cascaded from parent device or equipment)
   * @param {Object} node - Point node
   * @returns {boolean} True if point is under templated device or equipment (CASCADE)
   */
  const isTemplatedPoint = (node) => {
    // Check if point is under a templated device or templated equipment
    const hasParentDeviceTemplate = !!(node.parentDeviceTemplateId ||
              node.data?.parentDeviceTemplateId);
    const hasParentEquipmentTemplate = !!(node.parentEquipmentTemplateId ||
              node.data?.parentEquipmentTemplateId);

    return hasParentDeviceTemplate || hasParentEquipmentTemplate;
  };

  /**
   * Check if points can be added to equipment
   * @param {Object} node - Equipment node
   * @returns {boolean} True if equipment is not templated
   */
  const canAddPointsToEquipment = (node) => {
    return !isTemplatedEquipment(node);
  };

  /**
   * Get template information for a node
   * @param {Object} node - Node (equipment, device, or point)
   * @returns {Object|null} Template metadata or null if not templated
   */
  const getTemplateInfo = (node) => {
    if (!isTemplatedEquipment(node) && !isTemplatedDevice(node) && !isTemplatedPoint(node)) {return null;}

    const templateType = node.type === 'device' ? 'device' : (node.type === 'point' ? 'point' : 'equipment');

    // Check if point is under templated device or equipment
    if (node.type === 'point' && (node.parentDeviceTemplateId || node.parentEquipmentTemplateId)) {
      return {
        templateId: node.parentEquipmentTemplateId || node.parentDeviceTemplateId,
        templateName: node.parentEquipmentTemplateName || 'Parent Template',
        templateType: 'equipment',
        isFromParent: true,
      };
    }

    // Check if device is under templated equipment
    if (node.type === 'device' && node.parentEquipmentTemplateId) {
      return {
        templateId: node.parentEquipmentTemplateId,
        templateName: node.parentEquipmentTemplateName || 'Parent Equipment Template',
        templateType: 'equipment',
        isFromParent: true,
      };
    }

    // Check for controller template
    if (isController(node)) {
      const controllerTemplateName = node.data?.controller_template_name || node.controller_template_name || node.controllerTemplateName;
      if (controllerTemplateName) {
        return {
          templateId: node.data?.controller_template_id || node.controller_template_id,
          templateName: controllerTemplateName,
          templateType: 'controller',
          isFromParent: false,
        };
      }
    }

    return {
      templateId: node.data?.template_id || node.templateId,
      templateName: node.data?.template_name || node.templateName || 'Unknown Template',
      templateType: templateType,
      isFromParent: false,
    };
  };

  return {
    isController,
    isTemplatedEquipment,
    isTemplatedDevice,
    isTemplatedPoint,
    canAddPointsToEquipment,
    getTemplateInfo,
  };
}
